import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { HeroBackground } from "@/components/HeroBackground";
import { listPublishedPosts } from "@/lib/blog.functions";
import blogHero from "@/assets/mckay/roofing-service.webp.asset.json";
import { caCities } from "@/data/site";

export const Route = createFileRoute("/expert-tips/")({
  head: () => ({
    meta: [
      { title: "Roofing & Solar Tips for Southern California – McKay Expert Tips" },
      { name: "description", content: "Practical roofing and solar advice for Southern California homeowners: maintenance, repairs, replacements, batteries and EV charging." },
      { name: "keywords", content: "roofing tips Southern California, solar tips Southern California, roof maintenance advice, solar battery guide, Southern California home maintenance, McKay Roofing & Solar" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { name: "publisher", content: "McKay Roofing & Solar" },
      { property: "og:title", content: "Roofing & Solar Tips – McKay Expert Tips" },
      { property: "og:description", content: "Local roofing and solar tips for Southern California, updated regularly." },
      { property: "og:url", content: "https://mckay.steadyautogrowthdemo.com/expert-tips" },
    ],
    links: [
      { rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/expert-tips" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "McKay Expert Tips – Roofing & Solar",
          description: "Roofing and solar advice for Southern California homeowners from McKay Roofing & Solar.",
          url: "https://mckay.steadyautogrowthdemo.com/expert-tips",
          isPartOf: {
            "@type": "WebSite",
            name: "McKay Roofing & Solar",
            url: "https://mckay.steadyautogrowthdemo.com",
          },
        }),
      },
    ],
  }),
  component: ExpertTipsHub,
});

function ExpertTipsHub() {
  const { data, isLoading } = useQuery({
    queryKey: ["expert-tips-latest"],
    queryFn: () => listPublishedPosts({ data: { limit: 20 } }),
  });
  const posts = data?.posts ?? [];

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground src={blogHero.url} alt="Southern California home with new roof and solar panels" />
          <div className="relative mx-auto flex min-h-[85vh] max-w-5xl flex-col justify-center px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-28">
            <h1 className="text-5xl font-extrabold sm:text-6xl text-balance">Roofing & Solar Tips for Southern California</h1>
            <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">Real, local advice on roof maintenance, repairs, replacements, solar, batteries and EV charging – from the McKay crew.</p>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-primary">Latest articles</h2>
          {posts.length === 0 ? (
            <p className="mt-6 text-muted-foreground">Articles are being generated. Check back in a few hours.</p>
          ) : (
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((p: any) => (
                <Link
                  key={p.id}
                  to="/expert-tips/$citySlug/$slug"
                  params={{ citySlug: p.city_slug, slug: p.slug }}
                  className="group rounded-2xl border border-border bg-card p-6 transition hover:border-accent hover:shadow-lg"
                >
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
                    <span className={p.audience === "commercial" ? "text-secondary" : "text-accent"}>{p.audience}</span>
                    {p.city_slug !== "general" && (
                      <span className="text-muted-foreground">· {p.city_slug.replace(/-/g, " ")}</span>
                    )}
                  </div>
                  <h3 className="mt-3 text-lg font-bold text-primary group-hover:text-accent text-balance">{p.title}</h3>
                  {p.tldr && <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{p.tldr}</p>}
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="bg-muted/40 py-14">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-primary">Browse by city</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/expert-tips/$citySlug" params={{ citySlug: "general" }} className="rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold hover:border-accent hover:text-accent">General Southern California</Link>
              {caCities.map((c) => (
                <Link key={c.slug} to="/expert-tips/$citySlug" params={{ citySlug: c.slug }} className="rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold hover:border-accent hover:text-accent">
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <CTABlock />
      </main>
      <SiteFooter />
    </div>
  );
}
